<script setup lang="ts">
import { ref } from 'vue';
import { store as storeAsset } from '@/actions/App/Http/Controllers/ProjectAssetController';
import { Button } from '@/components/ui/button';
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogHeader,
    DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';

// ── Asset type ─────────────────────────────────────────────────────────
interface AssetItem {
    id: string;
    original_name: string;
    mime: string;
    size_bytes: number;
    src: string;
    caption?: string | null;
    alt?: string | null;
    created_at?: string;
}

// ── Props / Emits ──────────────────────────────────────────────────────
const props = defineProps<{
    open: boolean;
    projectId: string;
    assets: AssetItem[];
}>();

const emit = defineEmits<{
    insert: [options: { assetId: string; alt: string; caption: string }];
    /** Notify parent that a new asset was uploaded so it can refresh its list. */
    uploaded: [asset: AssetItem];
    close: [];
}>();

// ── Upload tab ─────────────────────────────────────────────────────────
const fileInput = ref<HTMLInputElement | null>(null);
const captionUpload = ref('');
const altUpload = ref('');
const uploading = ref(false);
const uploadError = ref<string | null>(null);

async function onFileSelected(e: Event) {
    const file = (e.target as HTMLInputElement).files?.[0];

    if (!file) {
        return;
    }

    uploading.value = true;
    uploadError.value = null;

    const formData = new FormData();
    formData.append('file', file);

    if (captionUpload.value.trim()) {
        formData.append('caption', captionUpload.value.trim());
    }

    if (altUpload.value.trim()) {
        formData.append('alt', altUpload.value.trim());
    }

    try {
        // Include CSRF token from meta tag (Inertia sets it)
        const token = (
            document.querySelector('meta[name="csrf-token"]') as HTMLMetaElement
        )?.content;
        const res = await fetch(storeAsset.url(props.projectId), {
            method: 'POST',
            headers: token ? { 'X-CSRF-TOKEN': token } : {},
            body: formData,
        });

        if (!res.ok) {
            const json = await res.json().catch(() => ({}));
            uploadError.value = json?.message ?? `Erreur ${res.status}`;

            return;
        }

        const asset: AssetItem = await res.json();
        emit('uploaded', asset);

        // Auto-insert the just-uploaded image
        emit('insert', {
            assetId: asset.id,
            alt: asset.alt ?? altUpload.value.trim(),
            caption: asset.caption ?? captionUpload.value.trim(),
        });

        resetUploadForm();
    } catch {
        uploadError.value = 'Erreur réseau. Veuillez réessayer.';
    } finally {
        uploading.value = false;
    }
}

function resetUploadForm() {
    captionUpload.value = '';
    altUpload.value = '';
    uploadError.value = null;

    if (fileInput.value) {
        fileInput.value.value = '';
    }
}

// ── Library tab ────────────────────────────────────────────────────────
const selectedId = ref<string | null>(null);
const captionLibrary = ref('');
const altLibrary = ref('');

function selectAsset(asset: AssetItem) {
    selectedId.value = asset.id;

    if (!altLibrary.value) {
        altLibrary.value = asset.alt ?? asset.original_name;
    }

    if (!captionLibrary.value) {
        captionLibrary.value = asset.caption ?? '';
    }
}

function insertSelected() {
    if (!selectedId.value) {
        return;
    }

    emit('insert', {
        assetId: selectedId.value,
        alt: altLibrary.value.trim(),
        caption: captionLibrary.value.trim(),
    });
    resetLibraryForm();
}

function resetLibraryForm() {
    selectedId.value = null;
    captionLibrary.value = '';
    altLibrary.value = '';
}

function onClose() {
    resetUploadForm();
    resetLibraryForm();
    emit('close');
}
</script>

<template>
    <Dialog :open="open" @update:open="(v) => !v && onClose()">
        <DialogContent class="max-w-lg">
            <DialogHeader>
                <DialogTitle>Insérer une figure</DialogTitle>
                <DialogDescription>
                    Importez une image raster ou sélectionnez une image déjà
                    ajoutée au projet.
                </DialogDescription>
            </DialogHeader>

            <Tabs default-value="upload" class="mt-2">
                <TabsList class="grid w-full grid-cols-2">
                    <TabsTrigger value="upload">Importer</TabsTrigger>
                    <TabsTrigger value="library">Bibliothèque</TabsTrigger>
                </TabsList>

                <!-- ── Upload tab ────────────────────────────────────── -->
                <TabsContent value="upload" class="space-y-4 pt-4">
                    <div class="space-y-1.5">
                        <Label for="fig-file">Fichier image</Label>
                        <input
                            id="fig-file"
                            ref="fileInput"
                            type="file"
                            accept="image/jpeg,image/png,image/webp"
                            :disabled="uploading"
                            class="block w-full cursor-pointer rounded border border-input bg-background px-3 py-2 text-sm file:mr-3 file:cursor-pointer file:rounded file:border-0 file:bg-primary file:px-2 file:py-1 file:text-xs file:font-medium file:text-primary-foreground hover:file:bg-primary/90 disabled:opacity-50"
                            @change="onFileSelected"
                        />
                    </div>

                    <div class="space-y-1.5">
                        <Label for="fig-caption-upload"
                            >Légende (optionnelle)</Label
                        >
                        <Input
                            id="fig-caption-upload"
                            v-model="captionUpload"
                            placeholder="Évolution des métriques…"
                            :disabled="uploading"
                        />
                    </div>

                    <div class="space-y-1.5">
                        <Label for="fig-alt-upload"
                            >Texte alternatif (accessibilité)</Label
                        >
                        <Input
                            id="fig-alt-upload"
                            v-model="altUpload"
                            placeholder="Graphique montrant…"
                            :disabled="uploading"
                        />
                    </div>

                    <p v-if="uploadError" class="text-sm text-destructive">
                        {{ uploadError }}
                    </p>

                    <p v-if="uploading" class="text-sm text-muted-foreground">
                        Téléversement en cours…
                    </p>
                </TabsContent>

                <!-- ── Library tab ───────────────────────────────────── -->
                <TabsContent value="library" class="pt-4">
                    <template v-if="assets.length === 0">
                        <p
                            class="py-6 text-center text-sm text-muted-foreground"
                        >
                            Aucune image dans ce projet. Importez-en une via
                            l'onglet "Importer".
                        </p>
                    </template>

                    <template v-else>
                        <ScrollArea class="h-56">
                            <div class="grid grid-cols-3 gap-2 p-1">
                                <button
                                    v-for="asset in assets"
                                    :key="asset.id"
                                    type="button"
                                    class="relative overflow-hidden rounded border-2 transition-colors"
                                    :class="
                                        selectedId === asset.id
                                            ? 'border-primary'
                                            : 'border-transparent hover:border-border'
                                    "
                                    @click="selectAsset(asset)"
                                >
                                    <img
                                        :src="asset.src"
                                        :alt="asset.alt ?? asset.original_name"
                                        class="aspect-square w-full object-cover"
                                    />
                                </button>
                            </div>
                        </ScrollArea>

                        <template v-if="selectedId">
                            <div class="mt-3 space-y-3">
                                <div class="space-y-1.5">
                                    <Label for="fig-caption-lib"
                                        >Légende (optionnelle)</Label
                                    >
                                    <Input
                                        id="fig-caption-lib"
                                        v-model="captionLibrary"
                                        placeholder="Ajouter une légende…"
                                    />
                                </div>

                                <div class="space-y-1.5">
                                    <Label for="fig-alt-lib"
                                        >Texte alternatif</Label
                                    >
                                    <Input
                                        id="fig-alt-lib"
                                        v-model="altLibrary"
                                        placeholder="Description de l'image…"
                                    />
                                </div>

                                <div class="flex justify-end">
                                    <Button @click="insertSelected"
                                        >Insérer</Button
                                    >
                                </div>
                            </div>
                        </template>
                    </template>
                </TabsContent>
            </Tabs>
        </DialogContent>
    </Dialog>
</template>
